import { useEffect, useState } from 'react';
import { requestSampleScan } from '../renderer/onboarding';
import { failureKind } from './upload';

/**
 * The tour's five sample pages going up to the job (`POST {channel}/import`). On a loaded machine the
 * request failed with no answer at all, and the tour's window went on saying "0 of 5 documents" for
 * good: nothing reads files that never arrived (journey, 2026-10-10). So the send is tried again by
 * itself, a few times, and if it still fails the window says the tour couldn't start, with Try again.
 *
 * Trying again is safe: the reading is started on the files a successful send's receipt names
 * (browser-captures.ts, `stageFiles`), so pages from a send whose answer was lost are never read,
 * and the tour's five are read once.
 */

/** Rests between the tries, in milliseconds: three sends in all. */
export const TOUR_FEED_RESTS: readonly number[] = [1000, 3000];

/** A failure worth sending again: no answer, or the server's own trouble; never a refusal. */
export function worthAnotherTry(error: unknown): boolean {
  const kind = failureKind(error);
  if (kind === 'network')
    return !(error instanceof Error && error.message.includes('PDFs or images'));
  const status = Number(kind.replace('refused_', ''));
  return status >= 500 || status === 408 || status === 429;
}

/** `send`, again after each rest while it fails in a way worth another try. */
export async function feedWithRetries<T>(
  send: () => Promise<T>,
  rests: readonly number[] = TOUR_FEED_RESTS,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((done) => setTimeout(done, ms)),
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await send();
    } catch (error) {
      if (attempt >= rests.length || !worthAnotherTry(error)) throw error;
      await wait(rests[attempt]!);
    }
  }
}

const FEED_FAILED_EVENT = 'paige-tour-feed-failed';
const failed = new Map<string, { corpus: string; documents?: string[] }>();

/** The tour's samples for `jobId` could not be sent after every try; the window offers Try again. */
export function noteTourFeedFailed(jobId: string, corpus: string, documents?: string[]): void {
  failed.set(jobId, { corpus, documents });
  window.dispatchEvent(new Event(FEED_FAILED_EVENT));
}

/** The samples went up: nothing to say. */
export function clearTourFeedFailed(jobId: string): void {
  if (!failed.delete(jobId)) return;
  window.dispatchEvent(new Event(FEED_FAILED_EVENT));
}

/** Sends the samples again, as the tour first did: the job's workspace takes the request and feeds them. */
export function retryTourFeed(jobId: string): void {
  const request = failed.get(jobId);
  if (!request) return;
  clearTourFeedFailed(jobId);
  // The same hand-over the tour's start made (it signals the workspace itself).
  requestSampleScan(jobId, request.corpus, request.documents);
}

/** Whether the tour's samples for `jobId` could not be sent after every try. */
export const tourFeedFailed = (jobId: string): boolean => failed.has(jobId);

/** The same, kept current for a screen. */
export function useTourFeedFailed(jobId: string | undefined): boolean {
  const [state, setState] = useState(() => Boolean(jobId && tourFeedFailed(jobId)));
  useEffect(() => {
    const update = () => setState(Boolean(jobId && tourFeedFailed(jobId)));
    update();
    window.addEventListener(FEED_FAILED_EVENT, update);
    return () => window.removeEventListener(FEED_FAILED_EVENT, update);
  }, [jobId]);
  return state;
}
